import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/store/auth'
import { useEffectiveClientId } from '@/hooks/useAuth'
import type { User } from '@/types/database'

/**
 * The person, as distinct from the publication.
 *
 * Names are written as first and last; `full_name` is derived in the database
 * by a trigger and is not writable from here, so the two can never disagree.
 * The update grant is column scoped for the same reason `is_admin` sits on this
 * table: the row level policy alone would let a user promote themselves.
 */

export function useProfile() {
  const userId = useAuthStore((s) => s.user?.id)
  return useQuery({
    queryKey: ['profile', userId],
    enabled: !!userId,
    queryFn: async (): Promise<User | null> => {
      const { data, error } = await supabase
        .from('users').select('*').eq('id', userId!).maybeSingle()
      if (error) throw error
      return data as User | null
    },
  })
}

export function useUpdateProfile() {
  const userId = useAuthStore((s) => s.user?.id)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (patch: { first_name?: string | null; last_name?: string | null }) => {
      const { error } = await supabase.from('users').update(patch).eq('id', userId!)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['profile', userId] }),
  })
}

/** Personal preferences: voice reminders, the medal plate handle, and how
 *  Emma looks. Column grants on users allow exactly these. */
export function useUpdatePrefs() {
  const userId = useAuthStore((s) => s.user?.id)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (patch: {
      voice_reminders?: boolean
      display_handle?: string | null
      emma_color?: string | null
      emma_skin?: 'seed' | 'cowboy' | null
      season_intro_seen_at?: string | null
      season_snooze_until?: string | null
    }) => {
      const { error } = await supabase.from('users').update(patch).eq('id', userId!)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['profile', userId] }),
  })
}

/** Two megabytes, matching the bucket's own limit so the check fails here with
 *  a sentence rather than at the edge with a status code. */
export const AVATAR_MAX_BYTES = 2 * 1024 * 1024
export const AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

export function useUploadAvatar() {
  const userId = useAuthStore((s) => s.user?.id)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (file: File) => {
      if (!AVATAR_TYPES.includes(file.type)) {
        throw new Error('That file is not an image we can use. Try a JPEG, PNG or WebP.')
      }
      if (file.size > AVATAR_MAX_BYTES) {
        throw new Error('That image is over 2 MB. Try a smaller one.')
      }

      const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg'
      // Owner's id first: the storage policy keys write access off that folder.
      // The timestamp is what busts the CDN cache, since the URL is public and
      // would otherwise keep serving the old face.
      const path = `${userId}/${Date.now()}.${ext}`

      const { error: upErr } = await supabase.storage
        .from('avatars').upload(path, file, { cacheControl: '3600', upsert: false })
      if (upErr) throw upErr

      const { data: pub } = supabase.storage.from('avatars').getPublicUrl(path)

      const { error: rowErr } = await supabase
        .from('users').update({ avatar_url: pub.publicUrl }).eq('id', userId!)
      if (rowErr) throw rowErr

      return pub.publicUrl
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['profile', userId] }),
  })
}

export function useRemoveAvatar() {
  const userId = useAuthStore((s) => s.user?.id)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('users').update({ avatar_url: null }).eq('id', userId!)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['profile', userId] }),
  })
}

/**
 * Changing the email address.
 *
 * Supabase sends a confirmation to the NEW address and does not switch until it
 * is clicked, which is the behaviour we want: nobody should be able to move an
 * account to an address they cannot read.
 */
export function useChangeEmail() {
  return useMutation({
    mutationFn: async (email: string) => {
      const { error } = await supabase.auth.updateUser({ email })
      if (error) throw error
    },
  })
}

export function useSendPasswordReset() {
  return useMutation({
    mutationFn: async (email: string) => {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/login`,
      })
      if (error) throw error
    },
  })
}

/** Initials for the well when there is no photo. Never more than two letters. */
export function initialsOf(name: string | null | undefined, email?: string | null): string {
  const source = (name ?? '').trim()
  if (source) {
    const parts = source.split(/\s+/)
    const first = parts[0]?.[0] ?? ''
    const last = parts.length > 1 ? parts[parts.length - 1][0] : ''
    return (first + last).toUpperCase()
  }
  return (email?.[0] ?? '?').toUpperCase()
}

/**
 * The account's kind (Brian's app-data.js PROFILES[...].kind and business):
 * a creator, or a business with its type, the line every post carries and the
 * check it runs (DEVELOPER-HANDOFF §2: "Realtors and other businesses follow
 * (they get a Business account layer)"). Every write carries it.
 */
export interface Business {
  account_kind: 'creator' | 'business'
  business_type: string | null
  business_line: string | null
  business_check: string | null
}

export function useBusiness() {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['business', clientId],
    enabled: !!clientId,
    queryFn: async (): Promise<Business | null> => {
      const { data, error } = await supabase.from('clients')
        .select('account_kind, business_type, business_line, business_check').eq('id', clientId!).maybeSingle()
      if (error) throw error
      return data as Business | null
    },
  })
}

export function useSetBusiness() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (b: Business) => {
      const { error } = await supabase.rpc('set_business' as never, {
        p_kind: b.account_kind, p_type: b.business_type, p_line: b.business_line, p_check: b.business_check,
      } as never)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['business', clientId] }),
  })
}

/** "Real estate business", or "Creator" (profile.html). */
export function kindLabel(b: Business | null | undefined): string {
  if (b?.account_kind !== 'business') return 'Creator'
  return b.business_type ? `${b.business_type} business` : 'Business'
}

